import { createContext, useContext, useEffect, useState } from "react";
import {
  getConfiguredIsGamified,
  getConfiguredStudyId,
  getStudySlug,
  isSingleStudy,
} from "../helper/study";
import { getWelcomeInfo } from "../services/weclome.service";
import { getConfig } from "../services/config.service";
import { ProfileType } from "../types/profile";
import { getStudyIsGamified } from "../services/study.service";
import { ENV } from "../environments/environment";
import i18n from "../i18n";
import { parseThemeTokens, ThemeTokens } from "../theme/tokens";
import { OnboardingConfig, parseOnboardingConfig } from "../helper/onboarding";

interface configValueType {
  study: {
    slug: string;
    id: number;
    is_gamified: boolean;
  };
  app: {
    name: string;
    logo: string;
    theme: string;
    /** Researcher-authored token theme (THEME study_config), parsed +
     *  validated; null/absent = fall back to the project default. */
    themeTokens?: ThemeTokens | null;
    /** Researcher-chosen onboarding mode (ONBOARDING study_config), parsed;
     *  null/absent = fall back to the per-project default onboarding. */
    onboarding?: OnboardingConfig | null;
  };
  selectedRole: ProfileType;
  setRole: (role: ProfileType) => void;
  /** Clear selectedRole ONLY when the committed one doesn't match roleId —
   *  reads fresh state, immune to stale closures in same-tick flows. */
  clearRoleIfMismatch: (roleId: number) => void;
}

const configValue: configValueType = {
  study: {
    slug: undefined,
    id: undefined,
    is_gamified: undefined,
  },
  app: undefined,
  selectedRole: undefined,
  setRole: () => {},
  clearRoleIfMismatch: () => {},
};

const configContext = createContext<configValueType>(configValue);

enum CONFIG_PARAMS {
  APP_NAME = "APP_NAME",
  COLOR_THEME = "COLOR_THEME",
  LOGO = "LOGO",
  THEME = "THEME",
  ONBOARDING = "ONBOARDING",
}

/** Generic fallback used when the study has no LOGO config row. Consumers that
 *  want "researcher-uploaded logo, else project art" compare against this to
 *  tell an actual upload apart from the context default. */
export const DEFAULT_LOGO = "/assets/images/logo.png";

const defaultConfig = {
  [CONFIG_PARAMS.APP_NAME]: ENV.APP_NAME,
  [CONFIG_PARAMS.LOGO]: DEFAULT_LOGO,
  [CONFIG_PARAMS.COLOR_THEME]: "MentalPlus",
};

const getConfigItem = (config, code: string) => {
  return config.find((item) => item.code === code)?.value;
};

const ConfigProvider = ({ children }) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [config, setConfig] = useState<configValueType>(configValue);
  const slug = getStudySlug();

  useEffect(
    function () {
      async function loadConfig() {
        try {
          let id: number;
          let is_gamified: boolean;

          if (isSingleStudy()) {
            // Single-study: id + is_gamified are pinned in ENV.STUDY — no welcome
            // lookup, no is_gamified call.
            id = getConfiguredStudyId() as number;
            is_gamified = getConfiguredIsGamified() as boolean;
          } else {
            // Multi-study: resolve the study via the URL slug, exactly as before.
            const {
              data: { id: resolvedId },
            } = await getWelcomeInfo({ slug, lang: "en" });
            id = resolvedId;
            is_gamified = (await getStudyIsGamified(id)).data;
          }

          const { data: config } = await getConfig(id);

          const appName =
            getConfigItem(config, CONFIG_PARAMS.APP_NAME) ||
            defaultConfig.APP_NAME;

          // Server config wins over the ENV default set at i18n init: update the
          // {{appName}} every t() call interpolates from here on.
          i18n.options.interpolation = {
            ...i18n.options.interpolation,
            defaultVariables: {
              ...i18n.options.interpolation?.defaultVariables,
              appName,
            },
          };

          const logo =
            getConfigItem(config, CONFIG_PARAMS.LOGO) || defaultConfig.LOGO;

          const theme =
            getConfigItem(config, CONFIG_PARAMS.COLOR_THEME) ||
            defaultConfig.COLOR_THEME

          // Researcher token theme: JSON string in the THEME config row.
          // Absent/invalid -> null, and ThemeContext falls back to the
          // per-project default tokens.
          const themeTokens = parseThemeTokens(
            getConfigItem(config, CONFIG_PARAMS.THEME)
          );

          // Researcher onboarding choice: JSON string in the ONBOARDING config
          // row. Absent/invalid -> null, and OnBoarding falls back to the
          // per-project default onboarding.
          const onboarding = parseOnboardingConfig(
            getConfigItem(config, CONFIG_PARAMS.ONBOARDING)
          );

          setConfig({
            ...config,
            study: { slug, id, is_gamified },
            app: {
              name: appName,
              logo: logo,
              theme: theme,
              themeTokens: themeTokens,
              onboarding: onboarding,
            },
          });
        } catch (e) {
          console.log(e);
        } finally {
          setIsInitialized(true);
        }
      }

      loadConfig();
    },
    [slug]
  );

  const setRole = function (role) {
    // Functional update: startSession and the landing pre-selection can both call
    // setRole in quick succession — a render-scoped `config` spread would clobber
    // whichever state landed in between.
    setConfig((prev) => ({
      ...prev,
      selectedRole: role,
    }));
  };

  // Clear a provably-wrong pre-selected role (cross-role login) WITHOUT trusting
  // any closure: the comparison reads the freshest committed selectedRole, so a
  // same-tick "setRole(picked) then startSession" signup flow can never have its
  // correct pick clobbered by a caller holding a stale snapshot.
  const clearRoleIfMismatch = function (roleId: number) {
    setConfig((prev) =>
      prev.selectedRole && prev.selectedRole.id !== roleId
        ? { ...prev, selectedRole: undefined }
        : prev
    );
  };

  if (!isInitialized) return <div />;

  return (
    <configContext.Provider value={{ ...config, setRole, clearRoleIfMismatch }}>
      {children}
    </configContext.Provider>
  );
};

const useConfig = () => useContext(configContext);

export { ConfigProvider, useConfig };
